import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useYearbook } from '../context/YearbookContext';
import { useFunnyLoadingText } from '../hooks';
import { 
  uploadCover, 
  fetchCoverAsBlob, 
  saveBookMetadata,
  stylizeCover,
  fetchStyledCoverAsBlob,
  getCoverGenerationsRemaining,
} from '../api/client';
import { Analytics } from '../utils/analytics';
import {
  PhotoIcon,
  BookOpenIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XMarkIcon,
  SparklesIcon,
  PaintBrushIcon,
} from '@heroicons/react/24/outline';

// Style options for cover generation
const COVER_STYLES = [
  { id: 'cartoon', name: 'Cartoon', emoji: '🎨' },
  { id: 'anime', name: 'Anime', emoji: '🌸' },
  { id: 'storybook', name: 'Storybook', emoji: '📖' },
  { id: 'vintage_comic', name: 'Comic', emoji: '💥' },
  { id: 'cyber', name: 'Cyber', emoji: '⚡' },
];

export default function BookSetupPage() {
  const navigate = useNavigate();
  const {
    isInitialized,
    sessionId,
    stats,
    bookTitle,
    bookSubtitle,
    bookDedication,
    coverImageUrl,
    setBookSetup,
    setCoverImage,
  } = useYearbook();
  
  // Local state for form
  const suggestedName = stats?.group_name
    || (stats?.participants?.length === 2
        ? `${stats.participants[0]} & ${stats.participants[1]}`
        : '');
  const [title, setTitle] = useState(bookTitle || suggestedName);
  const [subtitle, setSubtitle] = useState(bookSubtitle || '');
  const [dedication, setDedication] = useState(bookDedication || '');
  const [originalCoverUrl, setOriginalCoverUrl] = useState(null);
  const [coverPreview, setCoverPreview] = useState(coverImageUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const funnyLoadingText = useFunnyLoadingText();
  
  // Style generation state
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [isGeneratingStyle, setIsGeneratingStyle] = useState(false);
  const [styledCoverUrl, setStyledCoverUrl] = useState(null);
  const [generationsRemaining, setGenerationsRemaining] = useState(3);
  const [styleError, setStyleError] = useState(null);
  
  // Redirect to home if no valid session
  useEffect(() => {
    if (isInitialized && !sessionId) {
      navigate('/');
    }
  }, [isInitialized, sessionId, navigate]);
  
  // Load existing data on mount
  useEffect(() => {
    let isMounted = true;
    
    const loadExistingData = async () => {
      if (sessionId) {
        // Load cover image
        if (!coverPreview) {
          try {
            const blobUrl = await fetchCoverAsBlob(sessionId);
            if (isMounted) {
              setOriginalCoverUrl(blobUrl);
              setCoverPreview(blobUrl);
              setCoverImage(blobUrl);
            }
          } catch (err) {
            console.log('No existing cover image');
          }
        }
        
        // Load generations remaining
        try {
          const genData = await getCoverGenerationsRemaining(sessionId);
          if (isMounted) {
            setGenerationsRemaining(genData.generations_remaining);
          }
        } catch (err) {
          console.log('Could not fetch generations count');
        }
        
        // Try to load existing styled cover
        try {
          const { url: styledUrl, style } = await fetchStyledCoverAsBlob(sessionId);
          if (isMounted) {
            setStyledCoverUrl(styledUrl);
            setCoverPreview(styledUrl); // Show styled as main preview
            if (style) {
              setSelectedStyle(style); // Restore the style that was used
            }
          }
        } catch (err) {
          // No styled cover yet
        }
      }
    };
    
    loadExistingData();
    
    return () => {
      isMounted = false;
    };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle file drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleCoverUpload(e.dataTransfer.files[0]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleCoverUpload(e.target.files[0]);
    }
  };
  
  const handleCoverUpload = async (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB.');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    
    try {
      await uploadCover(sessionId, file);
      
      // Revoke old URLs
      if (originalCoverUrl && originalCoverUrl.startsWith('blob:')) {
        URL.revokeObjectURL(originalCoverUrl);
      }
      if (styledCoverUrl && styledCoverUrl.startsWith('blob:')) {
        URL.revokeObjectURL(styledCoverUrl);
      }
      
      const blobUrl = await fetchCoverAsBlob(sessionId);
      setOriginalCoverUrl(blobUrl);
      setCoverPreview(blobUrl);
      setCoverImage(blobUrl);
      
      // Clear styled cover when uploading new image
      setStyledCoverUrl(null);
      setSelectedStyle(null);
    } catch (err) {
      console.error('Cover upload error:', err);
      setError('Failed to upload cover image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleRemoveCover = () => {
    if (originalCoverUrl && originalCoverUrl.startsWith('blob:')) {
      URL.revokeObjectURL(originalCoverUrl);
    }
    if (styledCoverUrl && styledCoverUrl.startsWith('blob:')) {
      URL.revokeObjectURL(styledCoverUrl);
    }
    setOriginalCoverUrl(null);
    setCoverPreview(null);
    setCoverImage(null);
    setStyledCoverUrl(null);
    setSelectedStyle(null);
  };
  
  const handleGenerateStyle = async () => {
    if (!selectedStyle || !originalCoverUrl) return;
    
    if (generationsRemaining <= 0) {
      setStyleError('No generations remaining. Maximum 3 per session.');
      return;
    }
    
    setIsGeneratingStyle(true);
    setStyleError(null);
    
    try {
      const result = await stylizeCover(sessionId, selectedStyle);
      setGenerationsRemaining(result.generations_remaining);
      
      // Fetch the styled cover
      const { url: styledUrl } = await fetchStyledCoverAsBlob(sessionId);
      
      // Revoke old styled URL
      if (styledCoverUrl && styledCoverUrl.startsWith('blob:')) {
        URL.revokeObjectURL(styledCoverUrl);
      }
      
      setStyledCoverUrl(styledUrl);
      setCoverPreview(styledUrl); // Replace main preview with styled version
    } catch (err) {
      console.error('Style generation error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to generate styled cover. Please try again.';
      setStyleError(errorMessage);
    } finally {
      setIsGeneratingStyle(false);
    }
  };
  
  const handleUseOriginal = () => {
    setCoverPreview(originalCoverUrl);
    setSelectedStyle(null);
  };
  
  const handleContinue = async () => {
    if (!title.trim()) {
      setError('Please enter a book title.');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      await saveBookMetadata(sessionId, {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        dedication: dedication.trim() || null,
      });
      
      setBookSetup({
        title: title.trim(),
        subtitle: subtitle.trim(),
        dedication: dedication.trim(),
      });
      
      // Use current preview (styled or original)
      setCoverImage(coverPreview);
      
      Analytics.trackBookSetupComplete();
      navigate('/preview');
    } catch (err) {
      console.error('Save metadata error:', err);
      setError('Failed to save book details. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-rust/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <BookOpenIcon className="w-8 h-8 text-rust" />
        </div>
        <h1 className="font-display text-3xl font-bold text-ink mb-3">
          Design Your Book
        </h1>
        <p className="text-sepia max-w-md mx-auto">
          Give your yearbook a title and add a cover photo to make it truly special.
        </p>
      </div>
      
      {/* Main form - 2 columns */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Cover photo + Style selector */}
        <div>
          <h2 className="font-display text-lg font-semibold text-ink mb-4">
            Cover Photo
          </h2>
          
          {coverPreview ? (
            <div className="relative group">
              <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg border border-parchment">
                {isGeneratingStyle ? (
                  // Loading state
                  <div className="w-full h-full bg-gradient-to-br from-parchment via-cream to-parchment flex flex-col items-center justify-center">
                    <div className="w-12 h-12 border-3 border-rust/30 border-t-rust rounded-full animate-spin mb-4" />
                    <p className="text-ink font-medium">Creating magic...</p>
                    <p className="text-sepia/70 text-sm mt-1 italic px-8 text-center">{funnyLoadingText}</p>
                  </div>
                ) : (
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              
              {/* Styled badge */}
              {styledCoverUrl && coverPreview === styledCoverUrl && !isGeneratingStyle && (
                <div className="absolute top-3 left-3 px-3 py-1.5 
                  bg-white/80 backdrop-blur-md border border-white/40
                  text-ink/80 text-xs font-medium tracking-wide uppercase
                  rounded-lg shadow-lg shadow-ink/5
                  flex items-center gap-1.5
                  animate-fade-in">
                  <SparklesIcon className="w-3.5 h-3.5 text-rust" />
                  <span className="bg-gradient-to-r from-rust to-gold bg-clip-text text-transparent">
                    Styled
                  </span>
                </div>
              )}
              
              {/* Remove button */}
              {!isGeneratingStyle && (
                <button
                  onClick={handleRemoveCover}
                  className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full
                    flex items-center justify-center shadow-md
                    opacity-0 group-hover:opacity-100 transition-opacity
                    hover:bg-rust hover:text-white"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
              
              {/* Change photo button */}
              {!isGeneratingStyle && (
                <label className="absolute bottom-3 left-3 right-3
                  bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2
                  text-center cursor-pointer shadow-md
                  opacity-0 group-hover:opacity-100 transition-opacity
                  hover:bg-white text-sm font-medium text-ink"
                >
                  Change photo
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          ) : (
            // Upload zone
            <label
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`
                aspect-[3/4] rounded-xl border-2 border-dashed cursor-pointer
                flex flex-col items-center justify-center
                transition-all duration-200
                ${dragActive 
                  ? 'border-rust bg-rust/5 scale-[1.02]' 
                  : 'border-parchment hover:border-rust/50 hover:bg-cream/50'
                }
                ${isUploading ? 'opacity-50 pointer-events-none' : ''}
              `}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
              
              <div className={`
                w-16 h-16 rounded-2xl flex items-center justify-center mb-4
                ${dragActive ? 'bg-rust/20' : 'bg-parchment'}
              `}>
                {isUploading ? (
                  <div className="w-6 h-6 border-2 border-rust border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PhotoIcon className={`w-8 h-8 ${dragActive ? 'text-rust' : 'text-sepia'}`} />
                )}
              </div>
              
              <p className="font-medium text-ink mb-1">
                {isUploading ? 'Uploading...' : 'Drop your cover photo here'}
              </p>
              {isUploading ? (
                <p className="text-sm text-sepia/70 italic">{funnyLoadingText}</p>
              ) : (
                <p className="text-sm text-sepia">or click to browse</p>
              )}
              <p className="text-xs text-sepia/60 mt-3">
                JPG, PNG, or WebP up to 5MB
              </p>
            </label>
          )}
          
          {/* Style selector - shown when cover exists */}
          {originalCoverUrl && (
            <div className="mt-4 p-4 bg-parchment/30 rounded-xl border border-parchment">
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-ink">AI Style</span>
                <span className="text-xs text-sepia ml-auto">
                  {generationsRemaining}/3 left
                </span>
              </div>
              
              {/* Style buttons - horizontal row */}
              <div className="flex gap-1.5 mb-3">
                {COVER_STYLES.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setSelectedStyle(style.id)}
                    disabled={isGeneratingStyle}
                    className={`
                      flex-1 py-2 px-1 rounded-lg text-center transition-all text-xs
                      ${selectedStyle === style.id 
                        ? 'bg-rust text-white shadow-md' 
                        : 'bg-white border border-parchment hover:border-rust/30'
                      }
                      ${isGeneratingStyle ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <span className="text-base">{style.emoji}</span>
                    <p className={`mt-0.5 font-medium ${selectedStyle === style.id ? 'text-white' : 'text-ink'}`}>
                      {style.name}
                    </p>
                  </button>
                ))}
              </div>
              
              {/* Generate button */}
              <button
                onClick={handleGenerateStyle}
                disabled={!selectedStyle || isGeneratingStyle || generationsRemaining <= 0}
                className={`
                  w-full py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2
                  transition-all
                  ${selectedStyle && generationsRemaining > 0 && !isGeneratingStyle
                    ? 'bg-gradient-to-r from-rust to-gold text-white hover:shadow-lg'
                    : 'bg-parchment text-sepia/60 cursor-not-allowed'
                  }
                `}
              >
                <PaintBrushIcon className="w-4 h-4" />
                {isGeneratingStyle ? 'Generating...' : (styledCoverUrl ? 'Try Another Style' : 'Generate')}
              </button>
              
              {/* Use original button - shown when styled exists */}
              {styledCoverUrl && coverPreview === styledCoverUrl && !isGeneratingStyle && (
                <button
                  onClick={handleUseOriginal}
                  className="w-full mt-2 py-1.5 text-sm text-sepia hover:text-ink transition-colors"
                >
                  Use original photo instead
                </button>
              )}
              
              {/* Style error */}
              {styleError && (
                <p className="text-xs text-rust mt-2 text-center">{styleError}</p>
              )}
            </div>
          )}
          
          {/* Tip - only show if no cover yet */}
          {!originalCoverUrl && (
            <div className="mt-4 p-4 bg-gold/10 rounded-lg border border-gold/20">
              <p className="text-sm text-sepia">
                <span className="font-medium text-ink">Tip:</span> Use a photo that captures 
                your group's spirit - a memorable moment, a group selfie, or a meaningful place.
              </p>
            </div>
          )}
        </div>
        
        {/* Right: Book details */}
        <div>
          <h2 className="font-display text-lg font-semibold text-ink mb-4">
            Book Details
          </h2>
          
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-ink mb-2">
                Book Title <span className="text-rust">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., The Squad Chronicles"
                className="w-full px-4 py-3 rounded-lg border border-parchment
                  focus:border-rust focus:ring-2 focus:ring-rust/20 focus:outline-none
                  bg-white text-ink placeholder-sepia/40
                  font-display text-lg"
                maxLength={60}
              />
              <p className="text-xs text-sepia/60 mt-1 text-right">
                {title.length}/60
              </p>
            </div>
            
            {/* Subtitle */}
            <div>
              <label htmlFor="subtitle" className="block text-sm font-medium text-ink mb-2">
                Subtitle <span className="text-sepia/60">(optional)</span>
              </label>
              <input
                id="subtitle"
                type="text"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="e.g., A Year of Adventures"
                className="w-full px-4 py-3 rounded-lg border border-parchment
                  focus:border-rust focus:ring-2 focus:ring-rust/20 focus:outline-none
                  bg-white text-ink placeholder-sepia/40"
                maxLength={80}
              />
            </div>
            
            {/* Dedication */}
            <div>
              <label htmlFor="dedication" className="block text-sm font-medium text-ink mb-2">
                Dedication <span className="text-sepia/60">(optional)</span>
              </label>
              <textarea
                id="dedication"
                value={dedication}
                onChange={(e) => setDedication(e.target.value)}
                placeholder="To all the late-night conversations and unforgettable moments..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-parchment
                  focus:border-rust focus:ring-2 focus:ring-rust/20 focus:outline-none
                  bg-white text-ink placeholder-sepia/40 resize-none"
                maxLength={200}
              />
              <p className="text-xs text-sepia/60 mt-1 text-right">
                {dedication.length}/200
              </p>
            </div>
            
            {/* Preview card */}
            <div className="p-6 bg-gradient-to-br from-parchment/30 to-gold/10 rounded-xl border border-parchment">
              <div className="flex items-start gap-4">
                <div className="w-16 h-20 bg-white rounded shadow-sm flex items-center justify-center shrink-0 border border-parchment/50 overflow-hidden">
                  {coverPreview ? (
                    <img src={coverPreview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <BookOpenIcon className="w-6 h-6 text-sepia/30" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-display text-lg font-bold text-ink truncate">
                    {title || 'Your Book Title'}
                  </p>
                  {subtitle && (
                    <p className="text-sm text-sepia truncate">{subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mt-6 p-4 bg-rust/10 border border-rust/20 rounded-lg text-rust text-sm">
          {error}
        </div>
      )}
      
      {/* Navigation */}
      <div className="mt-12 flex items-center justify-between">
        <button
          onClick={() => navigate('/privacy')}
          className="flex items-center gap-2 text-sepia hover:text-ink transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>
        
        <button
          onClick={handleContinue}
          disabled={!title.trim() || isSaving}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <SparklesIcon className="w-5 h-5" />
              Generate Preview
              <ArrowRightIcon className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
